import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Users, CalendarClock, CheckCircle2, Clock3, TrendingUp, Megaphone, Wrench, FolderKanban, ListChecks, ClipboardList, AlertTriangle, Handshake, DollarSign, Trophy, XCircle } from "lucide-react";import { Link } from "react-router-dom";
import { StatusPill } from "../components/StatusPill";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PROJECT_STATUS_COLORS, PRIORITY_COLORS } from "../components/project/projectUtils";
import { LEAD_STATUS_COLORS } from "../components/leads/leadUtils";
import { useAuth } from "../context/AuthContext";
import { formatMoney, sumByCurrency } from "../lib/currency";
import { revenueByService, canSeeCosts } from "../lib/services";

export default function Overview() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [contents, setContents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [salesDash, setSalesDash] = useState(null);

  const seesSocial = ["admin", "ceo", "coo", "manager", "account_manager", "graphic_designer", "content_writer", "execution_specialist"].includes(user?.role);
  const seesDev = ["admin", "ceo", "coo", "manager", "project_manager", "developer", "tester"].includes(user?.role);
  const seesSales = ["admin", "coo", "manager", "ceo", "sales_head", "sales_member"].includes(user?.role);
  const defaultTab = seesSocial ? "social" : (seesDev ? "dev" : "sales");

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        seesSocial ? api.get("/clients") : Promise.resolve({ data: [] }),
        seesSocial ? api.get("/contents") : Promise.resolve({ data: [] }),
        seesDev ? api.get("/projects") : Promise.resolve({ data: [] }),
        seesSales ? api.get("/leads/dashboard") : Promise.resolve({ data: null }),
      ]);
      if (results[0].status === "fulfilled") setClients(results[0].value.data);
      if (results[1].status === "fulfilled") setContents(results[1].value.data);
      if (results[2].status === "fulfilled") setProjects(results[2].value.data);
      if (results[3].status === "fulfilled") setSalesDash(results[3].value.data);
    })();
  }, [seesSocial, seesDev, seesSales]);

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Command Center</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Agency Overview</h1>
          <p className="text-neutral-600 text-sm mt-1">Two workspaces. One command deck.</p>
        </div>
        <div className="text-[11px] font-mono text-neutral-500 uppercase tracking-wider">
          {new Date().toLocaleDateString(undefined, {weekday:"long", month:"short", day:"numeric", year:"numeric"})}
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList className="bg-[#FFFFFF] border border-[#E4E7EC] p-1 max-w-full overflow-x-auto justify-start">
          {seesSocial && (
            <TabsTrigger value="social" data-testid="overview-tab-social" className="data-[state=active]:bg-orange-500/15 data-[state=active]:text-orange-700 gap-2">
              <Megaphone className="w-3.5 h-3.5" /> Social Media
            </TabsTrigger>
          )}
          {seesDev && (
            <TabsTrigger value="dev" data-testid="overview-tab-dev" className="data-[state=active]:bg-orange-500/15 data-[state=active]:text-orange-700 gap-2">
              <Wrench className="w-3.5 h-3.5" /> Development
            </TabsTrigger>
          )}
          {seesSales && (
            <TabsTrigger value="sales" data-testid="overview-tab-sales" className="data-[state=active]:bg-orange-500/15 data-[state=active]:text-orange-700 gap-2">
              <Handshake className="w-3.5 h-3.5" /> Sales
            </TabsTrigger>
          )}
        </TabsList>

        {seesSocial && (
          <TabsContent value="social" className="space-y-6" data-testid="overview-section-social">
            <SocialSection clients={clients} contents={contents} />
          </TabsContent>
        )}
        {seesDev && (
          <TabsContent value="dev" className="space-y-6" data-testid="overview-section-dev">
            <DevSection projects={projects} clients={clients} />
          </TabsContent>
        )}
        {seesSales && (
          <TabsContent value="sales" className="space-y-6" data-testid="overview-section-sales">
            <SalesSection dash={salesDash} role={user?.role} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SocialSection({ clients, contents }) {
  const { user } = useAuth();
  const showCosts = canSeeCosts(user);
  const activeClients = clients.filter(c => c.status === "Active");
  const stats = {
    clients: activeClients.length,
    pending: contents.filter(c=>c.status==="Pending Approval").length,
    scheduled: contents.filter(c=>c.status==="Scheduled").length,
    published: contents.filter(c=>c.status==="Published").length,
    mrrByCurrency: sumByCurrency(activeClients, "monthly_retainer"),
    byService: revenueByService(activeClients),
    oneTimeByService: revenueByService(activeClients, "one_time"),
  };
  const upcoming = contents
    .filter(c=>["Scheduled","Approved"].includes(c.status))
    .sort((a,b)=> (a.publishing_date+a.publishing_time).localeCompare(b.publishing_date+b.publishing_time))
    .slice(0,8);

  return (
    <>
      <div className={`grid grid-cols-2 gap-4 ${showCosts ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
        <StatCard icon={<Users className="w-4 h-4"/>} label="Active Clients" value={stats.clients} tint="indigo" testid="stat-clients" />
        <StatCard icon={<Clock3 className="w-4 h-4"/>} label="Pending Approval" value={stats.pending} tint="amber" testid="stat-pending" />
        <StatCard icon={<CalendarClock className="w-4 h-4"/>} label="Scheduled" value={stats.scheduled} tint="blue" testid="stat-scheduled" />
        <StatCard icon={<CheckCircle2 className="w-4 h-4"/>} label="Published" value={stats.published} tint="violet" testid="stat-published" />
        {showCosts && (Object.keys(stats.mrrByCurrency).length === 0 ? (
          <StatCard icon={<TrendingUp className="w-4 h-4"/>} label="MRR" value={formatMoney(0)} tint="emerald" testid="stat-mrr" />
        ) : Object.entries(stats.mrrByCurrency).map(([cur, amt]) => (
          <StatCard key={cur} icon={<TrendingUp className="w-4 h-4"/>} label={`MRR · ${cur}`} value={formatMoney(amt, cur)} tint="emerald" testid={`stat-mrr-${cur.toLowerCase()}`} />
        )))}
      </div>

      {showCosts && <ServiceRevenue byService={stats.byService} />}
      {showCosts && Object.keys(stats.oneTimeByService).length > 0 && <ServiceRevenue byService={stats.oneTimeByService} title="One-time Payments by Service" testid="service-onetime" />}

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="panel p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg tracking-tight">Upcoming Posts</h3>
            <Link to="/calendar" className="text-xs font-mono text-orange-400 hover:text-orange-700 uppercase tracking-wider">Open Calendar →</Link>
          </div>
          <div className="space-y-2">
            {upcoming.length === 0 && <div className="text-sm text-neutral-500">Nothing on the horizon.</div>}
            {upcoming.map((c) => {
              const client = clients.find(x=>x.id===c.client_id);
              return (
                <div key={c.id} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white/[0.03] border border-transparent hover:border-[#E4E7EC] transition-colors" data-testid={`upcoming-item-${c.id}`}>
                  <div className="w-9 h-9 rounded-md bg-gradient-to-br from-orange-500/30 to-red-500/20 border border-orange-500/20 grid place-items-center text-[10px] font-mono">
                    {(client?.brand_name||"?").slice(0,2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.content_topic || c.content_type}</div>
                    <div className="text-[11px] font-mono text-neutral-500 uppercase">{client?.brand_name} · {c.platform} · {c.content_type}</div>
                  </div>
                  <div className="text-[11px] font-mono text-neutral-600">{c.publishing_date} {c.publishing_time}</div>
                  <StatusPill status={c.status} />
                </div>
              );
            })}
          </div>
        </section>

        <section className="panel p-6">
          <h3 className="font-bold text-lg tracking-tight mb-4">Clients</h3>
          <div className="space-y-2">
            {clients.length === 0 && <div className="text-sm text-neutral-500">No clients yet.</div>}
            {clients.map((c) => (
              <Link to={`/clients/${c.id}`} key={c.id} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-white/[0.03] transition-colors" data-testid={`client-quick-${c.id}`}>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500/40 to-cyan-500/30 grid place-items-center text-[10px] font-bold">
                  {(c.brand_name||"?").slice(0,2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.client_name}</div>
                  <div className="text-[11px] font-mono text-neutral-500 uppercase">{c.industry}</div>
                </div>
                <StatusPill status={c.status} />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function DevSection({ projects, clients }) {
  const active = projects.filter(p => p.status === "In Progress").length;
  const onHold = projects.filter(p => p.status === "On Hold").length;
  const completed = projects.filter(p => p.status === "Completed").length;
  const totalTasks = projects.reduce((a, p) => a + (p.counts?.tasks || 0), 0);
  const doneTasks = projects.reduce((a, p) => a + (p.counts?.completed_tasks || 0), 0);
  const totalReqs = projects.reduce((a, p) => a + (p.counts?.requirements || 0), 0);

  const highPriority = projects.filter(p => ["High", "Critical"].includes(p.priority) && p.status !== "Completed").slice(0, 6);
  const recent = [...projects].sort((a,b) => (b.created_at||"").localeCompare(a.created_at||"")).slice(0, 6);
  const clientName = (id) => clients.find(c => c.id === id)?.client_name || "—";

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={<FolderKanban className="w-4 h-4"/>} label="Total Projects" value={projects.length} tint="indigo" testid="dev-stat-total" />
        <StatCard icon={<TrendingUp className="w-4 h-4"/>} label="In Progress" value={active} tint="blue" testid="dev-stat-active" />
        <StatCard icon={<AlertTriangle className="w-4 h-4"/>} label="On Hold" value={onHold} tint="amber" testid="dev-stat-onhold" />
        <StatCard icon={<ListChecks className="w-4 h-4"/>} label="Tasks Done" value={`${doneTasks}/${totalTasks}`} tint="violet" testid="dev-stat-tasks" />
        <StatCard icon={<ClipboardList className="w-4 h-4"/>} label="Requirements" value={totalReqs} tint="emerald" testid="dev-stat-reqs" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="panel p-6 lg:col-span-2" data-testid="dev-high-priority">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg tracking-tight">High Priority Projects</h3>
            <Link to="/projects" className="text-xs font-mono text-orange-400 hover:text-orange-700 uppercase tracking-wider">Open Projects →</Link>
          </div>
          <div className="space-y-2">
            {highPriority.length === 0 && <div className="text-sm text-neutral-500">Nothing on fire. Nice.</div>}
            {highPriority.map(p => {
              const total = p.counts?.tasks || 0;
              const done = p.counts?.completed_tasks || 0;
              const pct = total ? Math.round(done / total * 100) : 0;
              return (
                <Link to={`/projects/${p.id}`} key={p.id} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white/[0.03] border border-transparent hover:border-[#E4E7EC] transition-colors" data-testid={`dev-hp-item-${p.id}`}>
                  <div className="w-9 h-9 rounded-md bg-gradient-to-br from-orange-500/30 to-red-500/20 border border-orange-500/20 grid place-items-center">
                    <FolderKanban className="w-4 h-4 text-orange-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.project_name}</div>
                    <div className="text-[11px] font-mono text-neutral-500 uppercase truncate">{p.project_id} · {clientName(p.client_id)} · {p.current_stage}</div>
                  </div>
                  <div className="w-24 hidden md:block">
                    <div className="h-1.5 rounded-full bg-[#E4E7EC] overflow-hidden"><div className="h-full bg-gradient-to-r from-orange-500 to-red-500" style={{ width: `${pct}%` }} /></div>
                    <div className="text-[10px] font-mono text-neutral-500 mt-1 text-right">{pct}%</div>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PRIORITY_COLORS[p.priority] || ""}`}>{p.priority}</span>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PROJECT_STATUS_COLORS[p.status] || ""}`}>{p.status}</span>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="panel p-6" data-testid="dev-recent">
          <h3 className="font-bold text-lg tracking-tight mb-4">Recent Projects</h3>
          <div className="space-y-2">
            {recent.length === 0 && <div className="text-sm text-neutral-500">No projects yet.</div>}
            {recent.map(p => (
              <Link to={`/projects/${p.id}`} key={p.id} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-white/[0.03] transition-colors" data-testid={`dev-recent-${p.id}`}>
                <div className="w-8 h-8 rounded-md bg-gradient-to-br from-orange-500/25 to-red-500/15 border border-orange-500/20 grid place-items-center text-[10px] font-bold">
                  {p.project_type?.[0] || "P"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.project_name}</div>
                  <div className="text-[11px] font-mono text-neutral-500 uppercase truncate">{p.project_type}</div>
                </div>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PROJECT_STATUS_COLORS[p.status] || ""}`}>{p.status}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function SalesSection({ dash, role }) {
  const [advanced, setAdvanced] = useState(null);
  const isLeader = ["admin", "manager", "ceo", "sales_head"].includes(role);
  const money = canSeeCosts({ role });
  useEffect(() => {
    if (!isLeader) return;
    api.get("/leads/dashboard/advanced").then(r => setAdvanced(r.data)).catch(() => setAdvanced(null));
  }, [isLeader]);
  if (!dash) return <div className="text-neutral-500 text-sm">Loading sales dashboard…</div>;
  const s = dash.stats || {};
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={<Handshake className="w-4 h-4"/>} label="Total Leads" value={s.total_leads || 0} tint="indigo" testid="sales-stat-total" />
        <StatCard icon={<TrendingUp className="w-4 h-4"/>} label="This Month" value={s.leads_this_month || 0} tint="blue" testid="sales-stat-month" />
        <StatCard icon={<CalendarClock className="w-4 h-4"/>} label="Follow-ups Today" value={s.followups_today || 0} tint="amber" testid="sales-stat-today" />
        <StatCard icon={<AlertTriangle className="w-4 h-4"/>} label="Overdue" value={s.followups_overdue || 0} tint="violet" testid="sales-stat-overdue" />
        {money && <StatCard icon={<DollarSign className="w-4 h-4"/>} label="Pipeline Value" value={moneyByCurrency(s.pipeline_value_by_currency, s.pipeline_value)} tint="emerald" testid="sales-stat-pipeline" />}
      </div>
      {isLeader && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={<Trophy className="w-4 h-4"/>} label="Won" value={s.won_count || 0} tint="emerald" testid="sales-stat-won" />
          <StatCard icon={<XCircle className="w-4 h-4"/>} label="Lost" value={s.lost_count || 0} tint="violet" testid="sales-stat-lost" />
          {money && <StatCard icon={<DollarSign className="w-4 h-4"/>} label="Won Value" value={moneyByCurrency(s.won_value_by_currency, s.won_value)} tint="emerald" testid="sales-stat-wonvalue" />}
          <StatCard icon={<DollarSign className="w-4 h-4"/>} label="Proposals Awaiting" value={s.proposals_awaiting || 0} tint="blue" testid="sales-stat-awaiting" />
        </div>
      )}

      {isLeader && advanced && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="sales-advanced">
          <section className="panel p-6 lg:col-span-2" data-testid="sales-funnel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg tracking-tight">Conversion Funnel</h3>
              <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider">{advanced.total} leads tracked</span>
            </div>
            <div className="space-y-2.5">
              {(advanced.funnel || []).map((f, i) => {
                const maxCount = Math.max(1, ...(advanced.funnel || []).map(x => x.count));
                const widthPct = Math.max(6, (f.count / maxCount) * 100);
                const dropOff = i > 0 ? Math.max(0, advanced.funnel[i-1].count - f.count) : 0;
                return (
                  <div key={f.stage} data-testid={`funnel-row-${i}`}>
                    <div className="flex items-center justify-between text-[10px] font-mono uppercase text-neutral-600 mb-1">
                      <span>{f.stage}</span>
                      <span>
                        <span className="text-neutral-800">{f.count}</span>
                        <span className="text-neutral-600 ml-2">{f.pct}%</span>
                        {i > 0 && dropOff > 0 && <span className="text-rose-400 ml-2">-{dropOff}</span>}
                      </span>
                    </div>
                    <div className="h-6 rounded-md bg-[#FFFFFF] border border-[#E4E7EC] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-end pr-2" style={{ width: `${widthPct}%` }}>
                        {f.count > 0 && widthPct > 15 && <span className="text-[9px] font-mono text-neutral-900/90">{f.count}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="panel p-6" data-testid="sales-leaderboard">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg tracking-tight">Leaderboard</h3>
              <Trophy className="w-4 h-4 text-orange-400" />
            </div>
            <div className="space-y-2">
              {(advanced.leaderboard || []).slice(0, 6).length === 0 && <div className="text-sm text-neutral-500">No sales activity yet.</div>}
              {(advanced.leaderboard || []).slice(0, 6).map((row, i) => (
                <div key={row.owner_id} className="flex items-center gap-3 py-2 px-2 rounded-lg border border-transparent hover:border-[#E4E7EC] hover:bg-white/[0.02]" data-testid={`leaderboard-row-${row.owner_id}`}>
                  <div className={`w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold shrink-0 ${i === 0 ? "bg-gradient-to-br from-orange-500 to-red-500 text-neutral-900" : "bg-[#FFFFFF] border border-[#E4E7EC] text-neutral-600"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{row.owner_name}</div>
                    <div className="text-[10px] font-mono text-neutral-500 uppercase">{row.won} won · {row.win_rate}% win rate</div>
                  </div>
                  {money && <div className="text-right shrink-0">
                    <div className="text-[10px] font-mono text-neutral-500">Won</div>
                    <div className="text-sm font-bold text-emerald-700">{row.won_by_currency && Object.keys(row.won_by_currency).length ? Object.entries(row.won_by_currency).map(([c,v])=>formatMoney(v,c)).join(" · ") : formatMoney(row.won_value||0)}</div>
                  </div>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {isLeader && (
        <div className="grid lg:grid-cols-3 gap-6">
          <section className="panel p-6 lg:col-span-2" data-testid="sales-leads-by-head">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg tracking-tight">Leads by Head</h3>
              <Link to="/leads" className="text-xs font-mono text-orange-400 hover:text-orange-700 uppercase tracking-wider">Open Leads →</Link>
            </div>
            <div className="space-y-2">
              {(dash.leads_by_head || []).length === 0 && <div className="text-sm text-neutral-500">No leads distributed yet.</div>}
              {(dash.leads_by_head || []).map(h => (
                <div key={h.head_id} className="flex items-center gap-3 py-2.5 px-3 rounded-lg border border-[#E4E7EC] bg-[#FFFFFF]/60" data-testid={`sales-head-${h.head_id}`}>
                  <div className="w-9 h-9 rounded-md bg-gradient-to-br from-orange-500/30 to-red-500/20 border border-orange-500/20 grid place-items-center text-[10px] font-mono">
                    {(h.head_name||"?").split(" ").map(x=>x[0]).slice(0,2).join("").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{h.head_name}</div>
                    <div className="text-[10px] font-mono text-neutral-500 uppercase">{h.count} leads · {h.won} won</div>
                  </div>
                  {money && <div className="text-right">
                    <div className="text-[10px] font-mono text-neutral-500">Value</div>
                    <div className="text-sm font-bold text-emerald-700">{h.value_by_currency && Object.keys(h.value_by_currency).length ? Object.entries(h.value_by_currency).map(([c,v])=>formatMoney(v,c)).join(" · ") : formatMoney(h.value||0)}</div>
                  </div>}
                </div>
              ))}
            </div>
          </section>

          <section className="panel p-6" data-testid="sales-status-breakdown">
            <h3 className="font-bold text-lg tracking-tight mb-4">Pipeline Distribution</h3>
            <div className="space-y-2">
              {(dash.proposals_by_status || []).filter(x => x.count > 0).map(x => (
                <div key={x.status} className="flex items-center gap-3">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${LEAD_STATUS_COLORS[x.status] || ""}`}>{x.status}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-[#E4E7EC] overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-red-500" style={{ width: `${Math.min(100, (x.count / Math.max(1, s.total_leads)) * 100)}%` }} />
                  </div>
                  <span className="text-[11px] font-mono text-neutral-700 w-8 text-right">{x.count}</span>
                </div>
              ))}
              {(dash.proposals_by_status || []).every(x => x.count === 0) && <div className="text-sm text-neutral-500">No leads to distribute yet.</div>}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function ServiceRevenue({ byService, title = "Monthly Retainer by Service", testid = "service-revenue" }) {
  const rows = Object.entries(byService).sort((a, b) => a[0] === "Unallocated" ? 1 : b[0] === "Unallocated" ? -1 : 0);
  const totals = {};
  rows.forEach(([, cur]) => Object.entries(cur).forEach(([c, v]) => { totals[c] = (totals[c] || 0) + v; }));
  return (
    <section className="panel p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-bold text-lg tracking-tight">{title}</h3>
        <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Active clients</span>
      </div>
      {rows.length === 0 && <div className="text-sm text-neutral-500">No retainers recorded for active clients yet.</div>}
      <div className="space-y-3">
        {rows.map(([svc, cur]) => (
          <div key={svc} data-testid={`${testid}-${svc.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between gap-3 text-sm flex-wrap">
              <span className={`font-medium ${svc === "Unallocated" ? "text-neutral-500" : ""}`}>{svc}</span>
              <span className="font-semibold">{Object.entries(cur).map(([c, v]) => formatMoney(v, c)).join(" · ")}</span>
            </div>
            <div className="mt-1.5 space-y-1">
              {Object.entries(cur).map(([c, v]) => (
                <div key={c} className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                  <div className={`h-full rounded-full ${svc === "Unallocated" ? "bg-neutral-300" : "bg-orange-500"}`} style={{ width: `${Math.max(2, (v / totals[c]) * 100)}%` }} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {byService.Unallocated && <div className="text-[11px] text-neutral-500 mt-4">"Unallocated" is retainer from clients with several services and no per-service amounts. Edit those clients to split it.</div>}
    </section>
  );
}

function moneyByCurrency(byCur, fallback) {
  const entries = Object.entries(byCur || {}).filter(([, v]) => v);
  if (!entries.length) return formatMoney(fallback || 0);
  return entries.map(([c, v]) => formatMoney(v, c)).join(" · ");
}

function StatCard({ icon, label, value, tint, testid }) {
  const tints = {
    indigo: "from-orange-500/20 to-orange-500/5 border-orange-500/20 text-orange-700",
    amber: "from-amber-500/20 to-amber-500/5 border-amber-500/20 text-amber-700",
    blue: "from-blue-500/20 to-blue-500/5 border-blue-500/20 text-blue-700",
    violet: "from-red-500/20 to-red-500/5 border-red-500/20 text-red-700",
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/20 text-emerald-700",
  }[tint];
  return (
    <div className={`panel p-5 relative overflow-hidden`} data-testid={testid}>
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${tints} opacity-40 blur-2xl`} />
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${tints} bg-gradient-to-br ${tints} font-mono text-[10px] uppercase tracking-wider`}>
        {icon} {label}
      </div>
      <div className="mt-3 text-2xl xl:text-3xl font-extrabold tracking-tight break-words">{value}</div>
    </div>
  );
}
